import { PageHeader } from '@/components/PageHeader';
import { SearchInput } from '@/components/SearchInput';
import { SortToggle } from '@/components/SortToggle';
import { cn } from '@/utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoFilter, IoSearch } from 'react-icons/io5';

interface CardsHeaderProps {
  id?: string;
  total: number;
  filter: string;
  sortOrder: string;
  searchValue: string;
  showFilterSelect: boolean;
  filterOptions: Array<{ label: string; value: string; visible?: boolean }>;
  onFilterChange: (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => void;
  onSortOrderChange: (order: 'asc' | 'desc') => void;
  onSearchChange: (value: string) => void;
}

const FilterPopover = ({
  filter,
  options,
  onFilterChange,
}: {
  filter: string;
  options: Array<{ label: string; value: string }>;
  onFilterChange: ComponentProps<typeof CardsHeader>['onFilterChange'];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-lg p-1 transition-colors hover:bg-gray-100"
      >
        <IoFilter
          className={cn('size-5 text-gray-500', isOpen && 'text-linko-light')}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            <div className="py-1">
              {options.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    onFilterChange({
                      target: { name: 'filter', value: option.value },
                    });
                    setIsOpen(false);
                  }}
                  className={cn(
                    'block w-full px-4 py-2 text-left text-sm',
                    filter === option.value
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CardsHeader = ({
  id,
  total,
  filter,
  sortOrder,
  searchValue,
  showFilterSelect,
  filterOptions,
  onFilterChange,
  onSortOrderChange,
  onSearchChange,
}: CardsHeaderProps) => {
  const { t } = useTranslation();
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  return (
    <div id={id} className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <PageHeader title={t('cardsList:title')} count={total} />
        <div className="flex items-center gap-2">
          {/* Desktop Controls */}
          <div className="hidden items-center gap-2 md:flex">
            <SearchInput value={searchValue} onChange={onSearchChange} />
          </div>
          {/* Mobile Controls */}
          <div className="flex items-center gap-1 md:hidden">
            <button
              onClick={() => setIsSearchVisible(!isSearchVisible)}
              className="rounded-lg p-1 transition-colors hover:bg-gray-100"
            >
              <IoSearch className="size-5 text-gray-500" />
            </button>
          </div>
          {/* Common Controls */}
          {showFilterSelect && (
            <FilterPopover
              filter={filter}
              options={filterOptions}
              onFilterChange={onFilterChange}
            />
          )}
          {filter !== 'my' && (
            <SortToggle
              sortOrder={sortOrder as 'asc' | 'desc'}
              onToggle={onSortOrderChange}
            />
          )}
        </div>
      </div>
      <AnimatePresence>
        {isSearchVisible && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="md:hidden"
          >
            <SearchInput value={searchValue} onChange={onSearchChange} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
